import * as Api from '@ViewModels';
import { normalizeTriggerAutomationStepSchedule, shouldUseAnchoredSchedule } from './triggerAutomationStepRules';

type BehaviorBranchScheduleStepType = Api.AutomationStepType | 'TriggerAutomation';

const TRIGGER_AUTOMATION_STEP_TYPE = 'TriggerAutomation';

/**
 * Switch-branch child templates (both BehaviorSwitch and regular Switch) inherit the
 * root automation's schedule requirements. The API publisher applies the same
 * anchoring rule — driven by the root's ShouldScheduleBeAnchored() — to every
 * descendant template (AutomationTemplatePublisher.cs lines 456 & 514).
 *
 * For birthday/renewal/custom-date roots, every child step must remain RelativeToAnchor.
 * Non-anchored roots use delays after automation start. Scheduled/conditional legacy
 * schedules are preserved to avoid silently dropping calendar or conditional timing fields.
 *
 * Naming retained as "behavior branch" for diff continuity; semantically applies to any
 * switch branch child.
 */
export function normalizeBehaviorBranchSchedule(
	schedule: Api.IAutomationStepSchedule | null | undefined,
	rootTrigger?: Api.IAutomationTrigger | null,
	stepType?: BehaviorBranchScheduleStepType
): Api.IAutomationStepSchedule | null | undefined {
	if (!schedule) {
		return schedule;
	}

	if (stepType === TRIGGER_AUTOMATION_STEP_TYPE) {
		return normalizeTriggerAutomationStepSchedule(schedule, rootTrigger, { normalizeToRootTrigger: true });
	}

	if (
		schedule.criteria === Api.AutomationStepScheduleCriteria.Conditional ||
		schedule.criteria === Api.AutomationStepScheduleCriteria.Scheduled
	) {
		return schedule;
	}

	if (schedule.criteria === Api.AutomationStepScheduleCriteria.Immediately) {
		// API publisher rejects Immediately on strictly-anchored roots (Birthday/Renewal/Custom-date
		// ResourceSelectors). Meeting/EventRegistration allow Immediately, so use the narrower
		// ResourceSelector-only predicate rather than shouldUseAnchoredSchedule here.
		const requiresAnchoredRewrite = Api.VmUtils.Automations.triggers.triggerUsesRelativeToAnchorScheduleCriteria(
			rootTrigger || undefined
		);
		return {
			...schedule,
			criteria: requiresAnchoredRewrite
				? Api.AutomationStepScheduleCriteria.RelativeToAnchor
				: Api.AutomationStepScheduleCriteria.Immediately,
			numberOfDays: 0,
			numberOfHours: 0,
			numberOfMinutes: 0,
		};
	}

	const useAnchoredCriteria = shouldUseAnchoredSchedule(rootTrigger);
	// AfterAutomationStart represents a non-negative offset from the start of the automation,
	// so any negative input must be flipped. RelativeToAnchor uses signed offsets where the
	// sign carries semantic meaning (negative = before the anchor, positive = after); stripping
	// the sign there silently rewrites "X days before the birthday" as "X days after."
	const normalizeNumber = useAnchoredCriteria
		? (value: number | null | undefined) => value ?? 0
		: (value: number | null | undefined) => Math.abs(value ?? 0);
	return {
		...schedule,
		criteria: useAnchoredCriteria
			? Api.AutomationStepScheduleCriteria.RelativeToAnchor
			: Api.AutomationStepScheduleCriteria.AfterAutomationStart,
		numberOfDays: normalizeNumber(schedule.numberOfDays),
		numberOfHours: normalizeNumber(schedule.numberOfHours),
		numberOfMinutes: normalizeNumber(schedule.numberOfMinutes),
	};
}
